import { useState } from "react";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useHasPermission } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNextNumberPreview } from "@/hooks/use-number-sequences";
import { formatCurrency } from "@/lib/utils";

import { ReceivablePdfButton } from "./pdf/receivable-pdf-button";
import { RECEIVABLE_STATUS_LABELS } from "./receivable-labels";
import { ReceivableNewPaymentForm } from "./receivable-new-payment-form";
import { ReceivablePaymentHistory } from "./receivable-payment-history";
import { useReceivablePaymentForm } from "./use-receivable-payment-form";

import type { Receivable } from "@artisancode/api-types";

export function ReceivablePaymentDialog({
  open,
  onOpenChange,
  receivable,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receivable: Receivable | null;
}) {
  const canCreate = useHasPermission("receivables.create");
  const canUpdate = useHasPermission("receivables.update");
  const canDelete = useHasPermission("receivables.delete");
  const canVerify = useHasPermission("receivables.verify");

  const { preview: sequencePreview, autoGenerate } =
    useNextNumberPreview("receivable");
  const previewSeq = autoGenerate ? sequencePreview : "";

  const {
    metaForm,
    paymentForm,
    onSubmitMeta,
    onSubmitPayment,
    toggleVerification,
    removePayment,
    isRecording,
    isSavingMeta,
  } = useReceivablePaymentForm(open, receivable);
  const isMobile = useIsMobile();
  const [confirmPayOpen, setConfirmPayOpen] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState<string | null>(
    null,
  );

  const description = receivable
    ? `${receivable.customerName || "-"} · ${receivable.items
        .map((i) => `${i.productName} (${i.qty}x)`)
        .join(", ")} · ${receivable.date}`
    : null;

  const footer = (
    <>
      {receivable && <ReceivablePdfButton receivable={receivable} />}
      <Button variant="outline" onClick={() => onOpenChange(false)}>
        Tutup
      </Button>
    </>
  );

  const content = receivable && (
    <div className="space-y-5 py-2">
      <div className="grid grid-cols-2 gap-2.5 rounded-lg border bg-muted/20 p-3 sm:grid-cols-4">
        <div>
          <p className="text-xs text-muted-foreground">Total</p>
          <p className="font-semibold text-sm">
            {formatCurrency(receivable.totalAmount)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Sisa</p>
          <p className="font-semibold text-sm text-destructive">
            {formatCurrency(receivable.remainingAmount)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Status</p>
          <Badge
            variant={receivable.status === "settled" ? "default" : "outline"}
            className="mt-0.5"
          >
            {RECEIVABLE_STATUS_LABELS[receivable.status]}
          </Badge>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Umur</p>
          <p className="font-medium text-sm">{receivable.ageDays} hari</p>
        </div>
      </div>

      <Form {...metaForm}>
        <form
          onSubmit={onSubmitMeta}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <FormField
            control={metaForm.control}
            name="collectorName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>PIC Penagihan</FormLabel>
                <FormControl>
                  <Input placeholder="Nama PIC (opsional)" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={metaForm.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Catatan</FormLabel>
                <FormControl>
                  <Input placeholder="Catatan (opsional)" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="sm:col-span-2">
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={!canUpdate || isSavingMeta}
            >
              {isSavingMeta ? "Menyimpan..." : "Simpan PIC & Catatan"}
            </Button>
          </div>
        </form>
      </Form>

      <ReceivablePaymentHistory
        payments={receivable.payments}
        canUpdate={canUpdate}
        canDelete={canDelete}
        canVerify={canVerify}
        toggleVerification={toggleVerification}
        removePayment={setDeletingPaymentId}
      />

      <ReceivableNewPaymentForm
        paymentForm={paymentForm}
        onSubmitPayment={paymentForm.handleSubmit(() =>
          setConfirmPayOpen(true),
        )}
        previewSeq={previewSeq}
        canCreate={canCreate}
        canVerify={canVerify}
        isRecording={isRecording}
      />

      <ConfirmDialog
        open={confirmPayOpen}
        onOpenChange={setConfirmPayOpen}
        title="Catat pembayaran?"
        description={`Pembayaran ${formatCurrency(
          Number(paymentForm.getValues("amount")) || 0,
        )} akan dicatat pada piutang ini dan mengurangi sisa tagihan.`}
        confirmLabel="Ya, Catat"
        variant="default"
        isConfirming={isRecording}
        onConfirm={async () => {
          await onSubmitPayment();
          setConfirmPayOpen(false);
        }}
      />

      <ConfirmDialog
        open={deletingPaymentId !== null}
        onOpenChange={(next) => !next && setDeletingPaymentId(null)}
        title="Hapus pembayaran?"
        description="Pembayaran dihapus dan sisa tagihan piutang dihitung ulang."
        onConfirm={() => {
          if (deletingPaymentId) removePayment(deletingPaymentId);
          setDeletingPaymentId(null);
        }}
      />
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="flex max-h-[92dvh] flex-col gap-0 overflow-hidden rounded-t-2xl p-4"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader className="p-0 pb-3 text-left">
            <SheetTitle>Detail Piutang</SheetTitle>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
          <div className="flex-1 overflow-y-auto">{content}</div>
          <SheetFooter className="flex-row justify-end border-t p-0 pt-3">
            {footer}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto p-4 sm:p-6 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Detail Piutang</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {content}
        <DialogFooter className="flex flex-row justify-end border-t pt-3 mt-4">
          {footer}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
